"use client";

import { useLayoutEffect, useMemo, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { EASE } from "@/lib/animations";
import {
  HEADLINE_COLORS,
  type HeadlineSegment,
} from "@/data/about-heritage";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

interface ScrollFillHeadlineProps {
  segments: HeadlineSegment[];
}

interface Word {
  text: string;
  type: HeadlineSegment["type"];
  finalColor: string;
  key: string;
}

export function ScrollFillHeadline({ segments }: ScrollFillHeadlineProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Flatten all segments into a single word stream
  const words = useMemo<Word[]>(() => {
    const flat: Word[] = [];
    segments.forEach((seg, segIdx) => {
      const finalColor =
        seg.type === "muted"
          ? HEADLINE_COLORS.mutedFinal
          : seg.type === "serif"
          ? HEADLINE_COLORS.serifFinal
          : HEADLINE_COLORS.normalFinal;

      seg.text.split(" ").forEach((w, wIdx) => {
        flat.push({
          text: w,
          type: seg.type,
          finalColor,
          key: `${segIdx}-${wIdx}`,
        });
      });
    });
    return flat;
  }, [segments]);

  useLayoutEffect(() => {
    const heading = headingRef.current;
    if (!heading) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const wordEls = heading.querySelectorAll<HTMLSpanElement>("[data-word]");
    if (!wordEls.length) return;

    // Reduced motion → snap all to their final colors, no animation
    if (reduced) {
      wordEls.forEach((el) => {
        const finalColor = el.dataset.finalColor || HEADLINE_COLORS.normalFinal;
        gsap.set(el, { color: finalColor, opacity: 1, y: 0 });
      });
      return;
    }

    // Initial state — faded pencil color, slightly offset for buttery reveal
    gsap.set(wordEls, {
      color: HEADLINE_COLORS.initial,
      opacity: 0.35,
      y: 8,
    });

    const ctx = gsap.context(() => {
      // ✦ Soft entrance blur → sharpen (once)
      gsap.fromTo(
        heading,
        { filter: "blur(6px)" },
        {
          filter: "blur(0px)",
          duration: 1.1,
          ease: EASE.out,
          scrollTrigger: {
            trigger: heading,
            start: "top 88%",
            once: true,
          },
        }
      );

      // ✦ Word-by-word scroll-driven fill using a single timeline for perfect sync
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: heading,
          // Start when heading enters viewport
          start: "top 82%",
          // Finish comfortably before the heading exits — guarantees full reveal
          end: "bottom 55%",
          scrub: 0.8, // smooth lag for silky feel
        },
      });

      // Animate each word independently to its own final color
      wordEls.forEach((el, i) => {
        const finalColor = el.dataset.finalColor || HEADLINE_COLORS.normalFinal;
        // Position each word slightly after the previous
        const position = i * 0.08;
        tl.to(
          el,
          {
            color: finalColor,
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
          },
          position
        );
      });
    }, heading);

    return () => ctx.revert();
  }, [words]);

  return (
    <h2
      ref={headingRef}
      className="relative text-[clamp(1.8rem,4.2vw,3.8rem)] font-extrabold leading-[1.12] tracking-tight will-change-[filter]"
    >
      {words.map((word) => (
        <span
          key={word.key}
          data-word
          data-final-color={word.finalColor}
          className={
            word.type === "serif"
              ? "inline-block mr-[0.28em] font-serif italic font-normal will-change-[color,transform,opacity]"
              : "inline-block mr-[0.28em] will-change-[color,transform,opacity]"
          }
          style={{ color: HEADLINE_COLORS.initial }}
        >
          {word.text}
        </span>
      ))}
    </h2>
  );
}